import AppleHero from "@/components/AppleHero";
import ToolLogoCarousel from "@/components/ToolLogoCarousel";
import AppleSection from "@/components/AppleSection";
import ServiceSplitSection from "@/components/ServiceSplitSection";
import ServiceProgressIndicator from "@/components/ServiceProgressIndicator";
import FAQSection from "@/components/FAQSection";
import Footer from "@/components/Footer";
import CaseStudiesCarousel from "@/components/CaseStudiesCarousel";
import QuickContactForm from "@/components/QuickContactForm";
import CapsuleCard from "@/components/CapsuleCard";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import NewsCard from "@/components/NewsCard";
import { capsulas } from "@/data/capsulas";
import { blogPosts } from "@/data/blogPosts";
import { Phone, Zap, Lightbulb, Mic, Palette, Globe, ClipboardList, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo, useCallback } from "react";
import { useSEO } from "@/hooks/useSEO";
import { usePageTracking, useTrackClick } from "@/hooks/useTracking";


const Index = () => {
  const trackClick = useTrackClick();

  usePageTracking();

  useSEO({
    title: "Globaltis IA - Soluciones de inteligencia artificial para pymes y autónomos",
    description: "Más de 20 años ayudando a pymes. Agentes de voz, automatizaciones y contenido IA con resultados medibles. Diagnóstico gratis.",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Globaltis IA",
        "url": "https://globaltisia.lovable.app",
        "logo": "https://globaltisia.lovable.app/favicon.ico",
        "description": "Soluciones de inteligencia artificial para autónomos y pequeñas empresas",
        "foundingDate": "2003",
        "telephone": "+34916651232",
        "sameAs": [],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+34916651232",
          "contactType": "customer service",
          "availableLanguage": "Spanish"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "¿Cuánto cuesta implementar IA en mi negocio?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Depende del alcance. Una auditoría inicial cuesta entre 300-500€. Un piloto funcional entre 1.500-3.000€. Una implementación completa de 5.000 a 15.000€. Mantenimiento desde 500€/mes."
            }
          },
          {
            "@type": "Question",
            "name": "No soy técnico. ¿Puedo usar esto igualmente?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sí, la mayoría de nuestros clientes no son técnicos. Trabajamos con restaurantes, clínicas, consultoras y e-commerce. Nos encargamos de toda la parte técnica."
            }
          },
          {
            "@type": "Question",
            "name": "¿En cuánto tiempo veo resultados?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Pizzería Don Pepe recuperó su inversión en 2 meses. NaturalFit aumentó ventas un 45% en 3 meses. Un piloto típico funciona en 2-4 semanas."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué diferencia hay entre las herramientas gratuitas y contratar vuestros servicios?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Las herramientas son guías gratuitas para aprender por tu cuenta. Contratar servicios es para soluciones a medida, integradas con tus sistemas, con soporte continuo."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué pasa si la IA falla o da respuestas incorrectas?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Configuramos controles de calidad, alertas y límites claros. Con soporte continuo monitorizamos y ajustamos según resultados reales."
            }
          },
          {
            "@type": "Question",
            "name": "¿Trabajáis solo con ciertos tipos de negocio?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. Trabajamos con cualquier pyme o autónomo con procesos repetitivos: atención al cliente, emails, contenido, documentación, citas."
            }
          }
        ]
      }
    ]
  });

  const topCapsulas = useMemo(() => {
    return [...capsulas]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 3);
  }, []);

  const latestPosts = useMemo(() => {
    return [...blogPosts]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 6);
  }, []);

  return (
    <div className="min-h-screen">
      {/* 1. Hero */}
      <AppleHero
        overline="Consultora IA para pymes · +20 años de experiencia"
        title={["Tu negocio atendiendo", "clientes 24/7.", "Sin contratar a nadie."]}
        cta="Cuéntanos tu caso"
        ctaLink="#contact"
        backgroundVideo="/veo-tutorial.mp4"
        proofResult={{
          text: "Pizzería Don Pepe:",
          highlight: "ROI en 2 meses",
        }}
      />

      {/* Barra de confianza */}
      <div className="bg-muted/50 border-b border-border/20">
        <div className="max-w-5xl mx-auto py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm font-light text-muted-foreground tracking-wide">
          <span>+20 años de experiencia</span>
          <span className="hidden sm:inline text-border">·</span>
          <span>+150 clientes acompañados</span>
          <span className="hidden sm:inline text-border">·</span>
          <span>{blogPosts.length} guías y tutoriales</span>
        </div>
      </div>



      {/* Intro Servicios */}
      <AppleSection background="white" padding="large">
        <div className="text-center max-w-4xl mx-auto space-y-12">
          <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
            Servicios Completos
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-foreground leading-tight">
            Todo lo que tu negocio necesita.<br />
            <span className="text-muted-foreground">Sin complicaciones.</span>
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl font-light text-muted-foreground leading-relaxed">
            Atender clientes a cualquier hora. Automatizar lo que te roba tiempo.
            Tener una web que vende. Crear contenido sin parar.<br className="hidden md:block" />
            Nosotros lo montamos, lo integramos con tus herramientas y te enseñamos a usarlo.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-x-8 gap-y-3 text-sm sm:text-base md:text-lg font-light text-muted-foreground pt-2">
            <span className="flex items-center gap-2"><span className="text-primary">✓</span> Sin conocimientos técnicos</span>
            <span className="flex items-center gap-2"><span className="text-primary">✓</span> Funcionando en semanas</span>
            <span className="flex items-center gap-2"><span className="text-primary">✓</span> Soporte continuo incluido</span>
          </div>
        </div>
      </AppleSection>

      {/* Servicios — 3 destacados */}
      <div id="services">
        <ServiceSplitSection
          icon={Globe}
          title="Presencia Digital Express"
          benefits={[
            "El 75% de tus clientes te buscan en Google antes de llamarte. Si no te encuentran, llaman a otro.",
            "Te creamos una web profesional en 5 días — con SEO, móvil y preparada para convertir visitas en contactos.",
            "Landing pages específicas para cada servicio que ofreces, con formularios y llamadas a la acción que funcionan.",
            "Sin jerga técnica, sin esperas de meses, sin sorpresas en la factura."
          ]}
          imageOnLeft={true}
          background="black"
          number="1"
          ctaText="Ver servicio"
          ctaLink="/servicios/presencia-digital"
        />

        <ServiceSplitSection
          icon={Mic}
          title="Atención al Cliente 24/7"
          benefits={[
            "Cada llamada que no contestas es un cliente que se va a la competencia. Con un agente de voz IA, eso no vuelve a pasar.",
            "Atiende por teléfono y WhatsApp las 24h: responde preguntas, cualifica leads y agenda citas automáticamente.",
            "Tú recibes un resumen de cada conversación y decides cuándo intervenir personalmente.",
            "Implementación en 2 semanas. Sin contratar personal. Sin horarios."
          ]}
          imageOnLeft={false}
          background="white"
          number="2"
          ctaText="Ver servicio"
          ctaLink="/servicios/atencion-cliente"
        />

        <ServiceSplitSection
          icon={Zap}
          title="Automatización de Procesos"
          benefits={[
            "Si tu equipo dedica horas a emails, facturas o seguimientos manuales, estás pagando sueldos por tareas que una máquina hace en segundos.",
            "Conectamos tus herramientas actuales — CRM, email, facturación — para que los datos fluyan solos entre ellas.",
            "Menos errores humanos, menos olvidos, menos estrés. Tu equipo se enfoca en lo que genera dinero.",
            "Clientes reales han pasado de 3 personas haciendo tareas repetitivas a 1 persona supervisando todo."
          ]}
          imageOnLeft={true}
          background="black"
          number="3"
          ctaText="Ver servicio"
          ctaLink="/servicios/automatizacion"
        />
      </div>

      {/* Más servicios — Grid compacto */}
      <AppleSection background="gray" padding="large">
        <div className="max-w-6xl mx-auto space-y-16">
          <div className="text-center space-y-4">
            <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
              También hacemos
            </p>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-light text-foreground">
              Más servicios
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <Link
              to="/servicios/contenido-ia"
              className="group bg-background p-8 sm:p-10 rounded-3xl border border-border/20 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 space-y-5"
            >
              <Palette className="h-10 w-10 text-foreground/70" />
              <h4 className="text-lg font-medium text-foreground leading-snug">
                Contenido Profesional con IA
                <span className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 inline-block transition-all duration-300">→</span>
              </h4>
              <p className="text-muted-foreground text-sm leading-relaxed font-light">
                Tu competencia publica cada día. Tú no tienes tiempo. Con IA generas vídeos, diseños y textos profesionales en minutos — 10× más contenido sin ampliar equipo ni contratar agencia.
              </p>
            </Link>

            <Link
              to="/servicios/gestion-administrativa"
              className="group bg-background p-8 sm:p-10 rounded-3xl border border-border/20 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 space-y-5"
            >
              <ClipboardList className="h-10 w-10 text-foreground/70" />
              <h4 className="text-lg font-medium text-foreground leading-snug">
                Gestión Administrativa Digital
                <span className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 inline-block transition-all duration-300">→</span>
              </h4>
              <p className="text-muted-foreground text-sm leading-relaxed font-light">
                Actas de reunión, informes y documentación interna que se generan solos. Tu equipo deja de buscar información y empieza a usarla. Bases de conocimiento accesibles al instante.
              </p>
            </Link>

            <Link
              to="/servicios/diagnostico-ia"
              className="group bg-background p-8 sm:p-10 rounded-3xl border border-border/20 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 space-y-5"
            >
              <Search className="h-10 w-10 text-foreground/70" />
              <h4 className="text-lg font-medium text-foreground leading-snug">
                Diagnóstico de Madurez IA
                <span className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 inline-block transition-all duration-300">→</span>
              </h4>
              <p className="text-muted-foreground text-sm leading-relaxed font-light">
                ¿No sabes por dónde empezar con la IA? Analizamos tu negocio, identificamos las oportunidades reales y te entregamos una hoja de ruta con ROI estimado. Sin compromiso, sin humo.
              </p>
            </Link>
          </div>

          <div className="text-center pt-4">
            <Link
              to="/servicios"
              className="group inline-flex items-center gap-3 text-lg md:text-xl font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>Ver todos los servicios</span>
              <span className="text-xl group-hover:translate-x-2 transition-transform duration-300">→</span>
            </Link>
          </div>
        </div>
      </AppleSection>

      <ServiceProgressIndicator serviceCount={3} />

      {/* Tool Logo Carousel — puente entre servicios y casos de éxito */}
      <ToolLogoCarousel />

      {/* 3. Casos de éxito */}
      <AppleSection background="white" padding="large">
        <div className="max-w-7xl mx-auto space-y-16">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-tight">
              Casos reales.<br/>
              <span className="text-muted-foreground">Resultados reales.</span>
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl font-light text-muted-foreground leading-relaxed">
              Negocios reales que automatizan y venden más con IA.<br/>
              Sin equipos técnicos. Sin grandes inversiones.
            </p>
          </div>
          
          <CaseStudiesCarousel />
        </div>
      </AppleSection>

      {/* 4. Herramientas */}
      <AppleSection id="capsulas-section" background="black" padding="large">
        <div className="max-w-7xl mx-auto space-y-20">
          <div className="text-center space-y-16">
            <div className="space-y-12">
              <p className="text-sm font-light tracking-[0.2em] text-white/60 uppercase">
                Actualidad en IA
              </p>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white leading-[1.1]">
                Domina las herramientas de IA.<br />
                <span className="text-white/60">Gratis. Actualizadas cada semana.</span>
              </h2>
              
              <div className="space-y-8 max-w-3xl mx-auto">
                <p className="text-lg sm:text-xl md:text-2xl text-white font-normal">
                  Videos. Presentaciones. Propuestas. Automatizaciones.
                </p>
                
                <p className="text-lg md:text-xl font-light text-white/80">
                  Cada semana probamos las herramientas más nuevas y te enseñamos a usarlas en tu negocio.
                </p>
                
                <p className="text-base md:text-lg font-light text-white/60 pt-4">
                  <a 
                    href="#contact" 
                    onClick={() => trackClick("capsulas-no-time-cta", "¿No tienes tiempo? Déjanos hacerlo")}
                    className="inline-flex items-center gap-2 border-b border-white/30 hover:border-white/50 hover:text-white transition-colors"
                  >
                    ¿No tienes tiempo? Déjanos hacerlo por ti →
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-14">
            {topCapsulas.map((capsula) => {
              const relatedPosts = capsula.relatedPostIds
                .map(id => blogPosts.find(post => post.id === id))
                .filter(Boolean)
                .slice(0, 3);

              return (
                <CapsuleCard
                  key={capsula.id}
                  id={capsula.id}
                  icon={capsula.icon}
                  title={capsula.title}
                  description={capsula.description}
                  difficulty={capsula.difficulty}
                  timeToImplement={capsula.timeToImplement}
                  relatedPosts={relatedPosts}
                />
              );
            })}
          </div>

          <div className="text-center">
            <Link 
              to="/capsulas"
              onClick={() => trackClick("ver-herramientas-cta", "Ver todas las herramientas")}
              className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-white hover:opacity-60 transition-opacity"
            >
              <span>Ver todas las herramientas</span>
              <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </AppleSection>

      {/* 5. News — Últimos artículos */}
      <AppleSection background="white" padding="large">
        <div className="max-w-7xl mx-auto space-y-16">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
              News
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-tight">
              Lo último en IA<br />
              <span className="text-muted-foreground">para tu negocio.</span>
            </h2>
            <p className="text-lg md:text-xl font-light text-muted-foreground">
              {blogPosts.length} artículos y creciendo cada semana.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 sm:gap-x-10 sm:gap-y-14">
            {latestPosts.map((post) => (
              <NewsCard key={post.id} post={post} />
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/blog"
              onClick={() => trackClick("ver-blog-cta", "Ver todos los artículos")}
              className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-foreground hover:opacity-60 transition-opacity"
            >
              <span>Ver todos los artículos</span>
              <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </AppleSection>

      {/* 6. Proceso */}
      <AppleSection background="gray" padding="large">
        <div className="max-w-5xl mx-auto space-y-16">
          <div className="text-center space-y-4">
            <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
              Así de fácil
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground text-center leading-tight">
              Cómo funciona
            </h2>
          </div>
          
          <div className="space-y-12 sm:space-y-16">
            <div className="space-y-2 text-center">
              <div className="text-5xl md:text-6xl font-bold text-primary/30">1</div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-light text-foreground">
                Nos cuentas qué necesitas
              </h3>
              <p className="text-lg md:text-xl text-muted-foreground">
                Una conversación de 15 minutos. En español claro.
              </p>
            </div>

            <div className="w-12 h-px bg-border mx-auto"></div>

            <div className="space-y-2 text-center">
              <div className="text-5xl md:text-6xl font-bold text-primary/30">2</div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-light text-foreground">
                Te mostramos cómo funciona
              </h3>
              <p className="text-lg md:text-xl text-muted-foreground">
                Una demo real con tu caso específico.
              </p>
            </div>

            <div className="w-12 h-px bg-border mx-auto"></div>

            <div className="space-y-2 text-center">
              <div className="text-5xl md:text-6xl font-bold text-primary/30">3</div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-light text-foreground">
                Lo implementamos juntos
              </h3>
              <p className="text-lg md:text-xl text-muted-foreground">
                En días, no meses. Con soporte continuo.
              </p>
            </div>
          </div>
        </div>
      </AppleSection>

      {/* 6. FAQ */}
      <AppleSection background="black" padding="normal">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-light tracking-wider text-white/60 dark:text-black/60 uppercase text-center mb-3">
            IA para Pymes y Autónomos
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-white dark:text-black text-center leading-tight mb-6">
            Preguntas Frecuentes
          </h2>
          <p className="text-lg md:text-xl font-light text-white/60 dark:text-black/60 text-center leading-relaxed mb-12">
            Las dudas más comunes sobre cómo implementar IA en tu negocio. Si no encuentras tu respuesta, escríbenos.
          </p>
          <FAQSection />
        </div>
      </AppleSection>

      {/* 7. Contacto */}
      <AppleSection background="white" padding="normal">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          <div className="space-y-8 animate-fade-in" id="contact">
            <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase">
              Hablemos
            </p>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-foreground">
              ¿No tienes tiempo<br />
              para aprender?<br />
              Cuéntanos qué<br />
              necesitas.
            </h2>
            
            <div className="space-y-4 sm:space-y-6 text-lg sm:text-xl md:text-2xl font-light text-muted-foreground leading-relaxed">
              <p>Videos. Presentaciones. Automatizaciones. Webs.</p>
              <p><span className="font-medium text-foreground">Trabajos puntuales</span> con entrega rápida.</p>
              <p>O si prefieres, <span className="font-medium text-foreground">proyectos completos</span> adaptados a tu negocio.</p>
            </div>
            
            <div className="space-y-4 pt-8">
              <a href="tel:+34916651232" className="flex items-center gap-3 text-lg font-light text-muted-foreground hover:text-foreground transition-colors group">
                <Phone className="h-5 w-5 group-hover:scale-110 transition-transform" />
                <span>+34 91 665 12 32</span>
              </a>
            </div>
          </div>
          
          <div className="animate-fade-in">
            <QuickContactForm />
          </div>
          
        </div>
      </AppleSection>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default Index;
